import { Btn, Field, INPUT, Sheet, Switch } from "../../../components/msd/ui";
import { policyLabel } from "../../../lib/access";
import { usePersonForm } from "../../SuperAdmin/useOrgEditors";
import { FieldGroup, RemoveBtn, TG_LIST, TG_ROW } from "./kit";

const SCOPE_LABEL = {
  workshop: "Works at",
  region: "Covers region",
  regions: "Covers regions",
  dealers: "Covers dealers",
  workshops: "Covers workshops",
};

/**
 * Adding or editing one person, with the same form rules as Super Admin's
 * PersonDialog (usePersonForm).
 *
 * @param onRemove  shows Remove in the footer; the caller asks first
 */
export default function PersonSheet({ open, onOpenChange, ix, person, atWorkshop, onRemove }) {
  const {
    form,
    setForm,
    errors,
    toggleId,
    setRole,
    submit,
    pending,
    role,
    noLogin,
    question,
    emailOptional,
    roleIsFixed,
  } = usePersonForm({ open, onOpenChange, tenantId: null, ix, person, atWorkshop });

  const close = () => onOpenChange(false);
  // Only what the person needs to know to act, not what the thing is.
  const note = person ? null : atWorkshop ? `Added to ${atWorkshop}.` : "They set their own password at sign-up.";

  const switches = (items, labelOf) => (
    <div className={TG_LIST}>
      {items.map((it) => (
        <div key={it.id} className={TG_ROW}>
          <b className="grow text-[15px] font-semibold">{labelOf(it)}</b>
          <Switch on={form.scope_ids.includes(it.id)} onChange={() => toggleId(it.id)} label={labelOf(it)} />
        </div>
      ))}
    </div>
  );

  return (
    <Sheet
      open={open}
      title={person ? "Edit person" : "Add person"}
      onClose={close}
      onSubmit={submit}
      testid="person-sheet"
      footer={
        <>
          {person && onRemove ? (
            <RemoveBtn onClick={() => onRemove(person)}>Remove</RemoveBtn>
          ) : null}
          <Btn onClick={close}>Cancel</Btn>
          <Btn type="submit" variant="pri" disabled={pending}>
            {person ? "Save" : "Add person"}
          </Btn>
        </>
      }
    >
      {note ? <div className="text-[13px] text-msd-muted">{note}</div> : null}

      <Field
        label="Email"
        optional={emailOptional && !(person && !noLogin)}
        hint={
          // An email that is already a login is fixed; only a contact with none can be given one.
          person && !noLogin ? "Their login. It can't be changed here." : emailOptional ? "Gives them a login." : undefined
        }
        error={errors.email}
      >
        <input
          type="email"
          className={INPUT}
          value={form.email}
          placeholder="name@brand.in"
          autoFocus={!person}
          // The email identifies the login, so changing it would be a
          // different person. Someone who has none yet can still be given
          // one, which is what provisions their account.
          disabled={!!person && !noLogin}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
      </Field>

      <Field label="Name">
        <input
          type="text"
          className={INPUT}
          value={form.name}
          placeholder="Full name"
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
      </Field>

      <Field label="Phone number" error={errors.number}>
        <input
          type="tel"
          className={INPUT}
          value={form.number}
          placeholder="+91 98765 43210"
          onChange={(e) => setForm({ ...form, number: e.target.value })}
        />
      </Field>

      <Field
        label="Role"
        error={errors.role}
        hint={!noLogin && role?.access_policy ? `Sees ${policyLabel(role.access_policy).toLowerCase()}` : undefined}
      >
        <select
          className={INPUT}
          value={form.role}
          disabled={roleIsFixed}
          onChange={(e) => setRole(e.target.value)}
        >
          {ix.roles.map((r) => (
            <option key={r.code} value={r.code}>
              {r.label}
            </option>
          ))}
        </select>
      </Field>

      {question ? (
        <FieldGroup label={SCOPE_LABEL[question]} error={errors.scope}>
          {question === "workshop" &&
            (atWorkshop ? (
              <div className="text-[15px] text-msd-ink">{atWorkshop}</div>
            ) : (
              <select
                className={INPUT}
                value={form.workshop_location}
                onChange={(e) => setForm({ ...form, workshop_location: e.target.value })}
              >
                <option value="">Pick a workshop</option>
                {ix.workshops.map((w) => (
                  <option key={w.id} value={w.workshop_location}>
                    {w.workshop_location}
                  </option>
                ))}
              </select>
            ))}
          {question === "region" && (
            <select className={INPUT} value={form.scope_id} onChange={(e) => setForm({ ...form, scope_id: e.target.value })}>
              <option value="">Pick a region</option>
              {ix.regions.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          )}
          {question === "regions" && switches(ix.regions, (r) => r.name)}
          {question === "dealers" && switches(ix.dealers, (d) => d.name)}
          {question === "workshops" && switches(ix.workshops, (w) => w.workshop_location)}
        </FieldGroup>
      ) : null}
    </Sheet>
  );
}
